<template>
  <div class="modal fade" :class="{ 'fullscreen': fullscreenToDoModal }" @keydown.esc="pressEsc" id="toDoModal"
    tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header todo-modal-header">
          <div class="todo-list-selector">
            <div class="d-flex align-items-center todo-list-selector-inner">
              <div v-show="showingCalendar" class="align-items-center date-picker-btn" @click="showCalendar()">
                <i class="bi-calendar-event mx-1"></i>
                <datepicker id="todo-date-picker-input" class="py-2" v-model="pickedDate" :locale="language"
                  :input-format='"dd/MM/yyyy"' :weekStartsOn="weekStartOnMonday" />
              </div>
              <div v-show="!showingCalendar" class="align-items-center date-picker-btn">
                <div class="align-items-center date-picker-btn py-2" id="customListDropDown" data-bs-toggle="dropdown">
                  <i class="bi-view-list mx-1"></i>
                  <div id="todo-list-select">{{ pickedCListName }}</div>
                </div>
                <ul class="dropdown-menu" aria-labelledby="customListDropDown">
                  <li v-for="option in cListOptions" :key="option.listId" :value="option.listId">
                    <button class="dropdown-item" type="button" @click="pickedCList = option.listId">
                      <i class="bi-check2" :style="{
                        visibility: option.listId == pickedCList ? 'visible' : 'hidden',
                      }"></i>
                      <span>{{ option.listName }}</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div v-if="showCL && showCal" class="d-flex align-items-center">
                <div class="selector-divider"></div>
                <i id="btnGroupDrop1" class="bi-chevron-down p-2" type="button" data-bs-toggle="dropdown"></i>
                <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                  <li>
                    <button class="dropdown-item" type="button" @click="showingCalendar = true">
                      <i class="bi-calendar-check"></i>
                      <span>{{ $t("settings.calendar") }}</span>
                    </button>
                  </li>
                  <li>
                    <button class="dropdown-item" type="button" @click="showingCalendar = false">
                      <i class="bi-view-list"></i>
                      <span>{{ $t("settings.customLists") }}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="todo-header-actions d-flex ms-auto align-items-center">
            <time-picker :time="todo.time" @time-selected="changeTime"></time-picker>
            <button
              type="button"
              class="todo-action-btn"
              :class="{ 'is-active': todo.alarm }"
              :title="$t('todoDetails.alarm')"
              :aria-label="$t('todoDetails.alarm')"
              :aria-pressed="!!todo.alarm"
              @click="changeAlarm"
            >
              <i :class="{ 'bi-bell': !todo.alarm, 'bi-bell-fill': todo.alarm }" aria-hidden="true"></i>
            </button>
            <repeating-event v-if="showingCalendar" :repeatingEvent="todo.repeatingEvent" :todo="todo"
              @repeatingEventSelected="changeRepeatingEvent"></repeating-event>
            <color-picker :color="todo.color" @color-selected="changeColor"></color-picker>
            <button
              id="btnTaskOptionMenu"
              type="button"
              class="todo-action-btn"
              data-bs-toggle="dropdown"
              :title="$t('todoDetails.actions')"
              :aria-label="$t('todoDetails.actions')"
            >
              <i class="bi-three-dots-vertical" aria-hidden="true"></i>
              <span class="todo-action-label">{{ $t("todoDetails.actions") }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="btnTaskOptionMenu">
              <li>
                <button class="dropdown-item" type="button" @click="copyTodo">
                  <i class="bi-clipboard"></i>
                  <span>{{ $t("donate.copy") }}</span>
                </button>
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <button class="dropdown-item" type="button" @click="removeTodo" data-bs-dismiss="modal">
                  <i class="bi-trash"></i> <span>{{ $t("ui.remove") }}</span>
                </button>
              </li>
              <li v-if="todo.repeatingEvent">
                <button class="dropdown-item" type="button" @click="removeAll" data-bs-dismiss="modal">
                  <i class="bi-trash"></i> <span>{{ $t("ui.removeAll") }}</span>
                </button>
              </li>
            </ul>
          </div>
          <button
            type="button"
            class="todo-header-close todo-action-btn todo-action-btn--close"
            ref="closeModal"
            data-bs-dismiss="modal"
            :title="$t('todoDetails.close')"
            :aria-label="$t('todoDetails.close')"
          >
            <i class="bi-x" aria-hidden="true"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="todo-detail-title-row">
            <button
              type="button"
              class="todo-detail-check"
              :aria-pressed="!!todo.checked"
              :aria-label="todo.checked ? 'Uncheck' : 'Check'"
              @click="toggleTodoChecked"
            >
              <span
                v-if="todo.color != 'none'"
                class="cicle-icon"
                :style="'color: ' + todo.color"
                :class="{
                  'bi-check-circle-fill': todo.checked,
                  'bi-circle-fill': !todo.checked,
                }"
              ></span>
              <span
                v-else
                class="cicle-icon"
                :class="{
                  'bi-check-circle': todo.checked,
                  'bi-circle': !todo.checked,
                }"
              ></span>
            </button>
            <div class="title-container">
              <div
                v-show="!editingTitle && todo.text != ''"
                class="todo-title"
                :class="{ 'completed-task': todo.checked }"
                @click="editTitle"
              >
                <span v-html="todoText"></span>
              </div>
              <div
                v-show="!editingTitle && todo.text == ''"
                class="todo-title todo-title-empty-title"
                @click="editTitle"
              >
                {{ $t("todoDetails.taskTitle") }}
              </div>
              <input
                v-show="editingTitle"
                class="todo-title-input"
                type="text"
                v-model="todo.text"
                ref="titleInput"
                :placeholder="$t('todoDetails.taskTitle')"
                @blur="doneEditTitle()"
                @keyup.enter="onTitleEnter"
              />
              <description-text-area :todoDesc="todo.desc"
                @updated-description="changeDescription"></description-text-area>
            </div>
          </div>
          <div class="mt-3"></div>
          <div class="horizontal-divider mb-0 mt-3"></div>
          <ul class="sub-tasks">
            <li v-for="(subTask, index) in todo.subTaskList" :key="index" class="sub-task">
              <div v-show="!subTask.editing" draggable="true" @dragstart="startDrag($event, index)" @dragover.prevent>
                <div class="d-flex flex-row align-items-center" :class="{ checked: subTask.checked }">
                  <button
                    type="button"
                    class="sub-task-check"
                    :aria-pressed="!!subTask.checked"
                    :aria-label="subTask.checked ? 'Uncheck' : 'Check'"
                    @click.stop="toggleSubTask(index)"
                  >
                    <span
                      class="cicle-icon"
                      :class="{
                        'bi-check-circle': subTask.checked,
                        'bi-circle': !subTask.checked,
                      }"
                    ></span>
                  </button>
                  <label
                    class="form-check-label"
                    @click="changeSubTaskClickhandler(index)"
                    @dragenter.self="onDragenter($event)"
                    @dragleave.self="onDragleave($event)"
                    @drop="onDrop($event, index)"
                    @dragover.prevent
                  >
                    <span v-html="linkifyText(subTask.text)"></span>
                  </label>
                  <i class="bi-trash mx-2" :title="$t('ui.remove')" @click="removeSubTask(index)"></i>
                </div>
              </div>
              <input v-show="subTask.editing" v-model="subTask.text" @blur="doneEditSubTask(index)"
                @keyup.enter="doneEditSubTask(index)" :ref="'subTaskEdit' + index" class="edit-sub-task" />
            </li>
            <div class="new-sub-task d-flex align-items-center">
              <label for="new-sub-task"><i class="bi-plus-square mx-3"></i></label>
              <input type="text" id="new-sub-task" :placeholder="$t('todoDetails.addSubTask')" autocomplete="off"
                @blur="addSubTask()" @keyup.enter="addSubTask()" v-model="newSubTask.text" ref="newSubTask" />
            </div>
          </ul>
          <todo-activity-section v-if="todo && todo.id" :todo-id="todo.id" />
        </div>
      </div>
    </div>
  </div>

  <div class="position-fixed bottom-0 end-0 p-3" style="z-index: 1056">
    <toast-message id="copiedTaskToClipboard" :text="$t('todoDetails.copiedTaskToClipboard')"></toast-message>
    <toast-message id="taskRemoved" :text="$t('todoDetails.taskRemoved')" :sub-text="'(' + $t('ui.undo') + ')'"
      @subTextClick="undoRemoveTask"></toast-message>
    <toast-message id="recurrentTaskRemoved" :text="$t('todoDetails.recurrentTaskRemoved')"></toast-message>
  </div>

  <comfirm-modal :id="'removeReModalToDoDetails'" :title="$t('ui.removeRepeatingTask')"
    :text="$t('ui.repeatingTaskRemoveConfirm')" :ico="'bi-x-circle'" :okText="$t('ui.remove')" @on-ok="removeAllComfirmed"
    @on-cancel="removeAllCanceled"></comfirm-modal>
</template>

<script>
import Datepicker from "vue3-datepicker";
import moment from "moment";
import toastMessage from "../../components/toastMessage";
import colorPicker from "./colorPicker";
import timePicker from "./timePicker";
import repeatingEvent from "./repeatingEvent";
import comfirmModal from "../../components/comfirmModal.vue";
import descriptionTextArea from "./descriptionTextArea.vue";
import todoActivitySection from "../../components/todoActivitySection.vue";
import { Dropdown } from "bootstrap";
import { resetMobileViewportZoom } from "../../helpers/mobileViewport";
import todoDetailMixin from "./todoDetailMixin";

export default {
  name: "toDoModal",
  mixins: [todoDetailMixin],
  props: {
    selectedTodo: { required: true, type: Object },
  },
  components: {
    colorPicker,
    Datepicker,
    toastMessage,
    timePicker,
    repeatingEvent,
    comfirmModal,
    descriptionTextArea,
    todoActivitySection,
  },
  mounted() {
    const el = document.getElementById("toDoModal");
    if (!el) return;
    // Opening click can land on header dropdown toggles once the modal appears under the cursor.
    this._onModalShow = () => {
      el.classList.add("todo-modal-arming");
      this.hideHeaderDropdowns();
    };
    this._onModalShown = () => {
      this.hideHeaderDropdowns();
      // Let the initiating click finish before re-enabling header toggles.
      requestAnimationFrame(() => {
        this.hideHeaderDropdowns();
        el.classList.remove("todo-modal-arming");
      });
    };
    this._onModalHidden = () => {
      el.classList.remove("todo-modal-arming");
      this.hideHeaderDropdowns();
      resetMobileViewportZoom();
    };
    el.addEventListener("show.bs.modal", this._onModalShow);
    el.addEventListener("shown.bs.modal", this._onModalShown);
    el.addEventListener("hidden.bs.modal", this._onModalHidden);
  },
  beforeUnmount() {
    const el = document.getElementById("toDoModal");
    if (!el) return;
    if (this._onModalShow) el.removeEventListener("show.bs.modal", this._onModalShow);
    if (this._onModalShown) el.removeEventListener("shown.bs.modal", this._onModalShown);
    if (this._onModalHidden) el.removeEventListener("hidden.bs.modal", this._onModalHidden);
  },
  methods: {
    hideHeaderDropdowns() {
      const el = document.getElementById("toDoModal");
      if (!el) return;
      el.querySelectorAll('[data-bs-toggle="dropdown"]').forEach((toggle) => {
        const instance = Dropdown.getInstance(toggle);
        if (instance) instance.hide();
      });
      el.querySelectorAll(".dropdown-menu.show").forEach((menu) => {
        menu.classList.remove("show");
      });
    },
    pressEsc() {
      if (document.activeElement && document.activeElement.id == "toDoModal") {
        if (this.$refs.closeModal) this.$refs.closeModal.click();
      }
    },
  },
  watch: {
    selectedTodo(newVal) {
      this.applySelectedTodo(newVal);
    },
    pickedDate(newVal) {
      if (this.loadingView) return;
      var newListId = moment(newVal).format("YYYYMMDD");
      if (this.todo && newListId != this.todo.listId) {
        this.moveToTodoList(newListId);
      }
    },
    pickedCList(newVal) {
      if (this.loadingView) return;
      this.moveToTodoList(newVal);
    },
  },
  computed: {
    fullscreenToDoModal() {
      return this.$store.getters.config.fullscreenToDoModal;
    },
  },
};
</script>

<style scoped lang="scss">
@import "/src/assets/style/globalVars.scss";

.modal-dialog {
  max-height: 80vh;

  .modal-content {
    height: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;

    .modal-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      max-height: min(50vh, calc(80vh - 120px));
      margin: 16px 0;
      padding: 0 16px;
    }
  }
}

/* Ignore the click that opened the modal so it cannot toggle header dropdowns. */
#toDoModal.todo-modal-arming .todo-header-actions,
#toDoModal.todo-modal-arming .todo-list-selector {
  pointer-events: none;
}

#toDoModal.fullscreen {
  .modal-dialog {
    margin: 0;
    height: 85%;
    /* PC 不再铺满 90% 宽，与列表栏大致对齐 */
    width: min(720px, 92vw);
    max-width: 720px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .sub-tasks {
      max-height: unset;
    }
  }
}

@media (max-width: 767.98px) {
  .modal-dialog {
    width: calc(100% - 24px);
    max-width: calc(100% - 24px);
    margin-left: auto;
    margin-right: auto;
    /* 优先跟可视视口走，键盘弹出时由 html.keyboard-open 再收紧 */
    max-height: min(78vh, var(--vv-height, 78vh));
    height: auto;

    .modal-content {
      height: auto;
      max-height: min(78vh, var(--vv-height, 78vh));
      overflow: hidden;
      display: flex;
      flex-direction: column;

      .modal-body {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
    }
  }

  #toDoModal.fullscreen {
    .modal-dialog {
      margin-left: auto;
      margin-right: auto;
      height: auto;
      max-height: min(78vh, var(--vv-height, 78vh));
      width: calc(100% - 24px);
      max-width: calc(100% - 24px);
      position: relative;
      top: auto;
      left: auto;
      transform: none;
    }
  }

  .todo-modal-header {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "list close"
      "actions actions";
    align-items: center;
    gap: 6px 4px;
    padding: 10px 10px 8px;
    min-width: 0;
  }

  .todo-list-selector {
    grid-area: list;
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }

  .todo-list-selector-inner {
    min-width: 0;
    max-width: 100%;
  }

  .todo-list-selector .date-picker-btn {
    min-width: 0;
    max-width: 100%;
  }

  .todo-header-close {
    grid-area: close;
    margin: 0;
  }

  .todo-header-actions {
    grid-area: actions;
    display: flex !important;
    justify-content: space-between;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 4px;
    width: 100%;
    margin: 0 !important;
    padding: 6px 2px 2px;
    border-top: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.14));
    min-width: 0;
  }

  .todo-header-actions :deep(.header-menu-icons),
  .todo-header-actions .header-menu-icons,
  .todo-header-actions .todo-action-btn {
    flex: 1 1 0;
    margin: 0 !important;
    min-width: 48px;
    min-height: 52px;
    padding: 6px 2px;
    font-size: 1.35rem;
    border-radius: 12px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }

  .todo-header-actions :deep(.dropdown-center),
  .todo-header-actions :deep(.header-menu-icons) {
    flex: 1 1 0;
  }

  .todo-action-btn--close {
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    font-size: 1.75rem;
    border-radius: 10px;
  }

  .todo-action-btn--close .todo-action-label {
    display: none;
  }

  .todo-list-selector :deep(#todo-date-picker-input) {
    width: 78px;
    font-size: 16px;
  }

  #todo-list-select {
    width: auto;
    max-width: 120px;
  }
}

.todo-modal-header {
  display: flex;
  align-items: center;
  min-width: 0;
}

.todo-list-selector {
  min-width: 0;
}

.todo-header-actions {
  flex-shrink: 0;
}

.todo-action-btn {
  appearance: none;
  border: none;
  background: transparent;
  color: inherit;
  margin-left: 6px;
  padding: 4px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.todo-action-btn i {
  font-size: inherit;
  line-height: 1;
}

.todo-action-label {
  display: none;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.1;
  opacity: 0.72;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.todo-action-btn.is-active {
  color: #0d6efd;
}

.dark-theme .todo-action-btn.is-active {
  color: #0a84ff;
}

.todo-header-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.todo-list-selector .bi-chevron-down {
  @include btn-icon;
}

.todo-list-selector .date-picker-btn {
  display: flex;
  @include btn-icon;
  padding: 0px;

  #todo-list-select {
    font-size: 15px;
  }
}

.todo-list-selector .selector-divider {
  height: 21px;
  width: 1px;
  background-color: #b9b9b9;
  margin: 0px 4px 0px 4px;
}

.todo-detail-title-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.todo-detail-check {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.todo-detail-check .cicle-icon {
  font-size: 22px;
  line-height: 1;
}

.todo-detail-check[aria-pressed="true"] .cicle-icon {
  opacity: 0.55;
}

.todo-title {
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  border: 2px solid transparent;
  padding: 10px 2px 1px 2px;
  cursor: text;
  word-break: break-word;
}

.todo-title-input {
  font-size: 18px;
  line-height: 22px;
  width: 100%;
  font-weight: 500;
  outline: unset;
  border: 2px solid black;
  border-radius: 3px;

  .dark-theme & {
    border: 1px solid rgba(255, 255, 255, 0.658);
    background-color: unset;
  }
}

.todo-title-empty-title {
  color: grey;
}



.dropdown-item {
  color: #3c3c3c;
}

.sub-tasks {
  list-style: none;
  padding: 0px 10px 10px 10px;
  margin: 0px;

  li>div {
    -webkit-user-drag: element;
  }

  .sub-task {
    border-bottom: 1px solid #eaecef;

    .dark-theme & {
      border-bottom: 1px solid #464647;
    }

    label {
      width: 100%;
      padding: 10px 5px 10px 0px;
      min-height: 38px;
      height: auto;
    }

    .sub-task-check {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;

      .cicle-icon {
        font-size: 18px;
        line-height: 1;
        display: block;
      }
    }

    i.bi-trash {
      color: #87888a;
      display: none;
      cursor: pointer;

      &:hover {
        color: black;
      }

      .checked & {
        opacity: 1 !important;
      }

      .dark-theme & {
        color: #babbbe;

        &:hover {
          color: white;
        }
      }
    }

    .drag-hover {
      color: rgba(157, 157, 157, 0.43);
      background-color: rgb(250, 249, 249);

      .dark-theme & {
        color: rgb(87, 87, 87);
        background-color: #1f1e20;
      }
    }

    &:hover {
      background-color: $btn-hover-bg-color;

      .dark-theme & {
        background-color: $dt-btn-hover-bg-color;
      }

      i.bi-trash {
        display: block;
      }
    }
  }

  .new-sub-task {
    padding: 0px 5px 0px 0px;
    width: 100%;

    i {
      color: lightgrey;
    }

    input {
      border: none;
      width: 100%;
      height: 38px;
      outline: unset;

      border: 2px solid transparent;

      .dark-theme & {
        background-color: unset;
      }

      &:focus {
        border: 2px solid black;
        border-radius: 3px;

        .dark-theme & {
          border: 1px solid rgba(255, 255, 255, 0.658);
          background-color: #21262d;
        }
      }
    }
  }

  .edit-sub-task {
    outline: unset;
    border: none;
    width: 100%;
    height: 38px;
    width: calc(100% - 48px);
    margin-left: 48px;
    border: 2px solid black;
    border-radius: 3px;

    .dark-theme & {
      border: 2px solid white;
      background-color: #21262d;
    }
  }
}

.sub-task .checked .sub-task-check,
.sub-task .checked label {
  opacity: 0.6;
}

.sub-task .checked label {
  text-decoration: line-through;
}

.title-container {
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 0;
  margin-top: 0;
}

.form-check-input {
  width: 1.3em !important;
  height: 1.3em !important;
}

.dark-theme .form-select {
  background-color: #15161e;
  border: 1px solid #30363d;
  color: #c9d1d9;
}

.form-select:focus {
  box-shadow: none;
}

.modal-dialog {
  width: calc(100% - 48px);
  max-width: 520px;

  @media (max-width: 767.98px) {
    width: calc(100% - 24px);
    max-width: calc(100% - 24px);
  }
}

.header-menu-icons {
  margin-left: 6px;
  @include btn-icon;
}

.header-menu-icons.bi-x {
  font-size: 1.9rem;
  padding: 0px;
}

.modal.modal-static .modal-dialog {
  transform: none;
}

#todo-list-select {
  padding: 0px;
  border: none;
  background-color: unset;
  cursor: pointer;
  width: 90px;
  font-size: 15px;
  line-height: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

@media (max-width: 767.98px) {
  .todo-action-label {
    display: block;
  }

  .todo-header-actions .todo-action-btn:active,
  .todo-header-actions :deep(.header-menu-icons:active) {
    background: rgba(120, 120, 128, 0.14);
  }

  .dark-theme .todo-header-actions .todo-action-btn:active,
  .dark-theme .todo-header-actions :deep(.header-menu-icons:active) {
    background: rgba(120, 120, 128, 0.28);
  }

  #todo-list-select {
    width: auto;
    max-width: 120px;
  }
}

.completed-task {
  text-decoration: line-through;
}
</style>
